import { Injectable, inject, signal } from '@angular/core';
import { NotificationApiService } from '../../api/notification-api.service';
import { LoggerService } from '../logger/logger.service';
import { environment } from '../../../../environments/environments';

@Injectable({
    providedIn: 'root',
})
export class PushNotificationService {
    private readonly notificationApi = inject(NotificationApiService);
    private readonly logger = inject(LoggerService);

    private readonly permissionStateSignal = signal<
        'default' | 'granted' | 'denied' | 'unsupported'
    >('default');
    readonly permissionState = this.permissionStateSignal.asReadonly();

    private readonly isSubscribedSignal = signal<boolean>(false);
    readonly isSubscribed = this.isSubscribedSignal.asReadonly();

    private initPromise: Promise<void> | null = null;

    /**
     * Initialise les notifications push pour un membre.
     * Si la permission est deja accordee, s'abonne automatiquement.
     * Si la permission n'a pas encore ete demandee, ne fait rien
     * (iOS exige un geste utilisateur pour le prompt).
     */
    async initializeForMember(authId: string): Promise<void> {
        if (this.initPromise) return this.initPromise;
        this.initPromise = this.doInitialize(authId);
        try {
            await this.initPromise;
        } finally {
            this.initPromise = null;
        }
    }

    private async doInitialize(authId: string): Promise<void> {
        if (!this.isBrowserSupported()) {
            this.permissionStateSignal.set('unsupported');
            this.logger.debug(
                'Push notifications non supportees sur ce navigateur.',
            );
            return;
        }

        this.updatePermissionState();
        const permission = this.permissionStateSignal();

        if (permission === 'granted') {
            // Permission deja accordee → s'assurer que la subscription existe
            await this.ensureSubscription(authId);
        }
        // Si 'default' ou 'denied' → ne rien faire automatiquement.
        // Le bouton dans Settings permettra d'activer.
    }

    /**
     * Demande la permission push et s'abonne.
     * Doit etre appele depuis un geste utilisateur (tap/click).
     */
    async subscribe(authId: string): Promise<boolean> {
        if (!this.isBrowserSupported()) return false;

        try {
            const registration = await this.getSwRegistration();
            if (!registration) return false;

            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(
                    environment.vapidPublicKey,
                ),
            });

            await this.saveSubscription(authId, subscription);
            this.isSubscribedSignal.set(true);
            this.updatePermissionState();
            this.logger.debug('Push subscription enregistree.');
            return true;
        } catch (error) {
            this.updatePermissionState();
            this.logger.warn('Echec de la subscription push:', error);
            return false;
        }
    }

    /**
     * Se desabonne des notifications push.
     */
    async unsubscribe(authId: string): Promise<void> {
        if (!this.isBrowserSupported()) return;

        try {
            const registration = await this.getSwRegistration();
            if (!registration) return;

            const subscription =
                await registration.pushManager.getSubscription();

            if (subscription) {
                await this.notificationApi.deleteSubscription(
                    authId,
                    subscription.endpoint,
                );
                await subscription.unsubscribe();
            }

            this.isSubscribedSignal.set(false);
            this.logger.debug('Push subscription supprimee.');
        } catch (error) {
            this.logger.warn('Echec du desabonnement push:', error);
        }
    }

    /**
     * Verifie et met a jour l'etat de la permission.
     */
    updatePermissionState(): void {
        if (!this.isBrowserSupported()) {
            this.permissionStateSignal.set('unsupported');
            return;
        }

        this.permissionStateSignal.set(Notification.permission);
    }

    /**
     * Recupere le service worker avec un timeout de 10s.
     * Retourne null si le SW n'est pas disponible.
     */
    private async getSwRegistration(): Promise<ServiceWorkerRegistration | null> {
        try {
            return await Promise.race([
                navigator.serviceWorker.ready,
                new Promise<null>((resolve) =>
                    setTimeout(() => resolve(null), 10_000),
                ),
            ]);
        } catch {
            return null;
        }
    }

    /**
     * Verifie si le navigateur supporte les notifications push.
     */
    private isBrowserSupported(): boolean {
        return (
            typeof window !== 'undefined' &&
            'Notification' in window &&
            'PushManager' in window &&
            'serviceWorker' in navigator
        );
    }

    /**
     * Verifie si une subscription existe et la sauvegarde en DB.
     */
    private async ensureSubscription(authId: string): Promise<void> {
        try {
            const registration = await this.getSwRegistration();
            if (!registration) return;

            const subscription =
                await registration.pushManager.getSubscription();

            if (subscription) {
                await this.saveSubscription(authId, subscription);
                this.isSubscribedSignal.set(true);
            }
            // Pas de subscription malgre permission granted →
            // l'utilisateur devra cliquer sur le bouton dans Settings
        } catch (error) {
            this.logger.warn(
                'Erreur lors de la verification de la subscription:',
                error,
            );
        }
    }

    private async saveSubscription(
        authId: string,
        subscription: PushSubscription,
    ): Promise<void> {
        const json = subscription.toJSON();
        const endpoint = json.endpoint!;
        const p256dh = json.keys!['p256dh'];
        const auth = json.keys!['auth'];

        await this.notificationApi.upsertSubscription(
            authId,
            endpoint,
            p256dh,
            auth,
        );
    }

    /**
     * Convertit une cle VAPID base64url en Uint8Array pour PushManager.subscribe().
     */
    private urlBase64ToUint8Array(
        base64String: string,
    ): Uint8Array<ArrayBuffer> {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');
        const rawData = atob(base64);
        const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));
        for (let i = 0; i < rawData.length; i++) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
}
